import { ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { Chip } from "@grantyd/ui";
import { ReactComponent as MenuIcon } from "@/assets/icons/menu.svg";
import { ReactComponent as CloseIcon } from "@/assets/icons/close.svg";
import { ReactComponent as Logo } from "@/assets/icons/logo-full.svg";
import styles from "./Navbar.module.css";
import { useNavbar } from "@/hooks/useNavbar";
import { navbarContent } from "@/data/navBar";
import { generateHashKey } from "@/utils/helpers";
import { useNavigate } from "react-router-dom";

declare global {
  interface ReactElement {
    children: ReactNode;
  }
}

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {
    setIsTimerTriggered,
    handleMouseEnter,
    handleMouseLeave,
    handleClick,
    getNavbarClasses,
    buttons,
  } = useNavbar(navbarContent.settings);

  const navigate = useNavigate();

  const navbarClasses = getNavbarClasses(
    styles.navbar,
    styles.navbarVisible,
    styles.navbarHidden,
    styles.navbarTransparent
  );

  const handleLinkClick = (path: string, url: boolean, e: React.MouseEvent) => {
    e.preventDefault();
    setIsMenuOpen(false);

    if (url) {
      const { origin, port } = new URL(path, import.meta.env.VITE_WEBSITE_URL);

      const sameOrigin =
        origin == window.location.origin || port == window.location.port;
      if (!sameOrigin) {
        window.open(path, "_blank");
      } else {
        navigate(path, { replace: true });
      }

      return;
    }

    const isOnHomepage = window.location.pathname === "/";

    if (!isOnHomepage) {
      navigate("/");
      return;
    }
    const section = document.querySelector(path);
    if (section) {
      section.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <header
      className={navbarClasses}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => handleMouseLeave(isMenuOpen)}
      onClick={handleClick}
    >
      <div className={styles.navbarContent}>
        <div className={styles.logo} onClick={() => navigate("/")}>
          <Logo />
        </div>
        <nav className={isMenuOpen ? styles.navOpen : styles.nav}>
          <ul className={styles.navLinks}>
            {navbarContent.links
              .filter((link) => link.visible)
              .map((link, index) => (
                <li key={generateHashKey(`link_${index}`)}>
                  {link.enabled ? (
                    <>
                      {link.newTag && <Chip>New!</Chip>}
                      <Link
                        to={link.path}
                        onClick={(e) => handleLinkClick(link.path, link.url, e)}
                      >
                        <p>{link.text}</p>
                      </Link>
                    </>
                  ) : (
                    <span className={styles.disabledLink}>{link.text}</span>
                  )}
                </li>
              ))}
          </ul>
          <div className={styles.authButtons}>{buttons}</div>
        </nav>
        <button
          className={styles.menuToggle}
          onClick={() => {
            setIsMenuOpen(!isMenuOpen);
            setIsTimerTriggered(false);
          }}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
        </button>
      </div>
    </header>
  );
};

export default Navbar;
